import React, { useEffect, useState, useMemo } from "react";
import { Modal, Spin, Empty, Input } from "antd";
import { DownOutlined, CloseCircleFilled, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { useCountryStore, CountryTypeEnum, RiskClass } from "@ifa/utils";
import type { Country } from "@ifa/utils";
import styles from "./index.module.css";

export type CountrySelectorMode = "country" | "phone";

export interface CountryCodeModalProps {
  open: boolean;
  onCancel: () => void;
  onSelect: (country: Country) => void;
  selectedValue?: string | number;
  /**
   * 模式：'country' 选择国家，'phone' 选择区号
   * @default 'phone'
   */
  type?: CountrySelectorMode;
  /**
   * 标题
   */
  title?: React.ReactNode;
}

export interface CountrySelectProps {
  value?: string | number;
  onChange?: (value: string | number | undefined, country?: Country) => void;
  placeholder?: string;
  type?: CountrySelectorMode;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
  allowClear?: boolean;
  size?: "large" | "middle" | "small";
}

const cx = (...classNames: Array<string | false | null | undefined>) => classNames.filter(Boolean).join(" ");

// 简单的内存缓存，避免重复请求
// let cachedCountries: Country[] | null = null;
// let cachedCountriesPromise: Promise<Country[]> | null = null;

// const fetchAllCountries = (): Promise<Country[]> => {
//   if (cachedCountries) return Promise.resolve(cachedCountries);
//   if (cachedCountriesPromise) return cachedCountriesPromise;
//
//   cachedCountriesPromise = getCountryGroupList()
//     .then((response) => {
//       let list: Country[] = [];
//       if (Array.isArray(response)) {
//         list = response;
//       } else {
//         const data = response as any;
//         list = [...(data.hotCountries || []), ...(data.groupCountries || [])];
//       }
//       // 去重
//       const uniqueList = Array.from(new Map(list.map((item) => [item.id, item])).values());
//       cachedCountries = uniqueList;
//       return uniqueList;
//     })
//     .catch((err) => {
//       cachedCountriesPromise = null;
//       throw err;
//     });
//
//   return cachedCountriesPromise;
// };

export const CountrySelect: React.FC<CountrySelectProps> = ({ value, onChange, placeholder, type = "phone", disabled = false, readOnly = false, className, style, allowClear = false, size }) => {
  const { allCountries, fetchCountryList } = useCountryStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(undefined);
  // const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (allCountries.length === 0) {
      fetchCountryList();
    }
  }, [allCountries.length, fetchCountryList]);

  // 初始化或 value 变化时，查找对应的 Country 对象以便回显
  useEffect(() => {
    const findCountry = async () => {
      if (value === undefined || value === null || value === "") {
        setSelectedCountry(undefined);
        return;
      }

      // 如果当前选中的对象已经匹配 value，就不需要重新查找
      if (selectedCountry) {
        if (type === "country" && (selectedCountry.id === value || selectedCountry.countryCode === value)) return;
        if (type === "phone" && String(selectedCountry.areaCode) === String(value)) return;
      }

      // setLoading(true);
      try {
        const list = allCountries;
        const found = list.find((c) => {
          if (type === "country") return c.id === value || c.countryCode === value;
          // 比较区号时转为字符串比较
          return String(c.areaCode) === String(value);
        });
        setSelectedCountry(found);
      } catch (error) {
        console.error("Failed to fetch country list for select:", error);
      } finally {
        // setLoading(false);
      }
    };

    findCountry();
  }, [value, type, selectedCountry, allCountries]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(undefined, undefined);
    setSelectedCountry(undefined);
  };

  const displayText = useMemo(() => {
    if (!selectedCountry) return null;
    if (type === "phone") {
      return (
        <span className="flex items-center gap-2">
          <span className={cx("flex items-center", styles.countryName)}>
            {selectedCountry.fbCountryCode && <span className={cx("mr-2 px-1.5 py-0.5 text-xs rounded border", styles.phoneCodeBadge)}>{selectedCountry.fbCountryCode}</span>}+
            {selectedCountry.areaCode}
          </span>
        </span>
      );
    }
    return (
      <span className="flex items-center gap-2 overflow-hidden text-[16px]">
        {selectedCountry.flag && <span className="text-lg shrink-0">{selectedCountry.flag}</span>}
        <span className={cx("truncate", styles.countryName)}>{selectedCountry.cname || selectedCountry.name}</span>
      </span>
    );
  }, [selectedCountry, type]);

  const sizeClass = size === "small" ? "ant-input-sm" : size === "middle" ? "" : "ant-input-lg";

  return (
    <>
      <div
        className={`ant-input ${sizeClass} ${cx(
          "flex items-center justify-between whitespace-nowrap cursor-pointer group border px-[11px]",
          styles.selectTrigger,
          disabled || readOnly ? `cursor-not-allowed ${styles.selectTriggerDisabled}` : styles.selectTriggerEnabled
        )} ${
          disabled || readOnly ? "" : ""
        } ${className || ""}`}
        style={{
          fontSize: 16,
          lineHeight: 1.5,
          borderRadius: 8,
          ...style,
        }}
        onClick={() => {
          if (!disabled && !readOnly) {
            setModalOpen(true);
          }
        }}
      >
        <div className="flex-1 overflow-hidden mr-2  leading-[38px]">
          {displayText ? displayText : <span className={styles.placeholderText}>{placeholder || (type === "phone" ? "請選擇區號" : "請選擇國家")}</span>}
        </div>

        {!disabled && !readOnly && (
          <div className={cx("shrink-0 flex items-center", styles.triggerIcons)}>
            {allowClear && value && <CloseCircleFilled className={cx("hidden group-hover:inline-block mr-1 transition-colors", styles.clearIcon)} onClick={handleClear} />}
            <DownOutlined className="text-[10px]" />
          </div>
        )}
      </div>

      <CountryCodeModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        type={type}
        selectedValue={value}
        onSelect={(country) => {
          const newValue = type === "country" ? country.countryCode : country.areaCode;
          onChange?.(newValue, country);
          setSelectedCountry(country);
          setModalOpen(false);
        }}
      />
    </>
  );
};

export const CountryCodeModal: React.FC<CountryCodeModalProps> = ({ open, onCancel, onSelect, selectedValue, type = "phone" }) => {
  const { hotCountries, lowRiskCountries, mediumRiskCountries, highRiskCountries, otherCountries, count, loading, fetchCountryListByType } = useCountryStore();
  const [activeType, setActiveType] = useState<CountryTypeEnum>(CountryTypeEnum.Hot);
  const [searchText, setSearchText] = useState("");

  // Modal 打开时加载初始数据，关闭时重置状态
  useEffect(() => {
    if (open) {
      fetchCountryListByType(CountryTypeEnum.Hot);
    } else {
      setSearchText("");
      setActiveType(CountryTypeEnum.Hot);
    }
  }, [open, fetchCountryListByType]);

  // 切换分类时按需加载数据
  useEffect(() => {
    if (open && activeType !== CountryTypeEnum.Hot) {
      fetchCountryListByType(activeType);
    }
  }, [activeType, open, fetchCountryListByType]);

  // 根据分类获取对应的数据
  const getCountriesByType = (t: CountryTypeEnum): Country[] => {
    switch (t) {
      case CountryTypeEnum.Hot:
        return hotCountries;
      case CountryTypeEnum.LowRisk:
        return lowRiskCountries;
      case CountryTypeEnum.MediumRisk:
        return mediumRiskCountries;
      case CountryTypeEnum.HighRisk:
        return highRiskCountries;
      case CountryTypeEnum.Other:
        return otherCountries;
      default:
        return [];
    }
  };

  const filteredByType = getCountriesByType(activeType);

  // 从接口返回的 count 数据中获取数量
  const getCountByType = (t: CountryTypeEnum) => {
    return count[t] || 0;
  };

  const menuItems = [
    { label: "熱門推薦", value: CountryTypeEnum.Hot, count: getCountByType(CountryTypeEnum.Hot) },
    { label: "低風險地區", value: CountryTypeEnum.LowRisk, count: getCountByType(CountryTypeEnum.LowRisk) },
    { label: "中風險地區", value: CountryTypeEnum.MediumRisk, count: getCountByType(CountryTypeEnum.MediumRisk) },
    { label: "高風險地區", value: CountryTypeEnum.HighRisk, count: getCountByType(CountryTypeEnum.HighRisk) },
    { label: "其他地區", value: CountryTypeEnum.Other, count: getCountByType(CountryTypeEnum.Other) },
  ];

  // 前端本地搜尋过滤
  const filteredCountryList = useMemo(() => {
    if (!searchText.trim()) {
      return filteredByType;
    }
    const lowerSearchText = searchText.toLowerCase();
    return filteredByType.filter(
      (c) =>
        c.cname?.toLowerCase().includes(lowerSearchText) ||
        c.name?.toLowerCase().includes(lowerSearchText) ||
        c.countryCode?.toLowerCase().includes(lowerSearchText) ||
        c.fbCountryCode?.toLowerCase().includes(lowerSearchText) ||
        String(c.areaCode).includes(lowerSearchText),
    );
  }, [filteredByType, searchText]);

  const getRiskTag = (riskClass?: RiskClass) => {
    switch (riskClass) {
      case RiskClass.LowRisk:
        return <span className={cx("inline-flex items-center py-[3px] px-2 rounded-[6px] text-[12px] font-medium leading-[16px]", styles.riskTagLow)}>低風險</span>;
      case RiskClass.MediumRisk:
        return <span className={cx("inline-flex items-center py-[3px] px-2 rounded-[6px] text-[12px] font-medium leading-[16px]", styles.riskTagMedium)}>中風險</span>;
      case RiskClass.HighRisk:
        return <span className={cx("inline-flex items-center py-[3px] px-2 rounded-[6px] text-[12px] font-medium leading-[16px]", styles.riskTagHigh)}>高風險</span>;
      default:
        return null;
    }
  };

  const isSelectedCountry = (country: Country) => {
    if (selectedValue === undefined || selectedValue === null || selectedValue === "") return false;
    if (type === "country") {
      return country.id === selectedValue || country.countryCode === selectedValue;
    }
    return String(country.areaCode) === String(selectedValue);
  };

  const renderCountryCard = (country: Country) => {
    const isSelected = isSelectedCountry(country);
    return (
      <div
        key={country.id || country.countryCode}
        className={cx(
          "flex items-center py-[10px] px-[12px] rounded-[10px] border cursor-pointer transition-all duration-200 ease-in-out",
          styles.countryCard,
          isSelected && styles.countryCardSelected
        )}
        onClick={() => {
          onSelect(country);
          onCancel();
        }}
      >
        {/* Left: Country Code */}
        <div className={cx("shrink-0 w-[42px] h-[42px] rounded-[6px] border flex items-center justify-center font-[800] text-[14px] mr-2", styles.countryCodeBox)}>
          {country.fbCountryCode}
        </div>

        {/* Middle: Names */}
        <div className="flex flex-col flex-1 min-w-0 justify-center gap-[4px]">
          <span className={cx("text-[16px] font-medium leading-[22px] truncate", styles.countryCardTitle)}>{country.cname}</span>
          <span className={cx("text-[12px] font-medium leading-[16px] truncate", styles.countryCardSubtitle)}>{country.name}</span>
        </div>

        {/* Right: Tags (Risk + Area Code) */}
        <div className="shrink-0 ml-auto flex items-center gap-2">
          {/* Risk Tag - 固定宽度确保对齐 */}
          <div className="w-[58px] flex justify-end">{getRiskTag(country.riskClass)}</div>

          {/* Area Code Tag - 固定宽度确保对齐 */}
          <span className={cx("inline-flex items-center justify-center w-[59px] h-[24px] rounded-[6px] border text-[12px] font-medium", styles.areaCodeTag)}>
            +{country.areaCode}
          </span>
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={940}
      centered
      closable={false}
      className="country-code-modal"
      styles={{
        container: {
          padding: 0,
          overflow: "hidden",
          height: 560,
          display: "flex",
          flexDirection: "column",
          borderRadius: 16,
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
        body: { padding: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
      }}
    >
      <div className={cx("relative flex flex-col h-full", styles.modalRoot)}>
        {/* Header */}
        <div className={cx("flex items-center justify-between h-[64px] shrink-0 border-b px-[19px] py-[12px]", styles.modalHeader)}>
          <div className={cx("text-[18px] font-bold", styles.modalTitle)}>選擇國家地區</div>

          {/* Search Input in Header */}
          <div className="flex-1 max-w-[320px] mx-4">
            <Input
              prefix={<SearchOutlined className={styles.searchIcon} />}
              placeholder="搜尋國家名稱、代碼..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              className={styles.searchInput}
            />
          </div>

          <CloseOutlined onClick={onCancel} className={cx("text-[16px] cursor-pointer p-2 -mr-2", styles.closeButton)} />
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className={cx("w-[200px] flex flex-col p-[10px] overflow-y-auto", styles.sidebar)}>
            {menuItems.map((item) => {
              const isActive = activeType === item.value;
              return (
                <div
                  key={item.value}
                  className={cx(
                    "flex items-center justify-between h-[46px] px-[10px] mb-[4px] cursor-pointer transition-all rounded-[8px] border",
                    isActive ? styles.sidebarItemActive : styles.sidebarItemInactive
                  )}
                  onClick={() => setActiveType(item.value)}
                >
                  <div className="flex items-center gap-[10px]">
                    <div className={cx("w-[6px] h-[6px] rounded-full", isActive ? styles.sidebarDotActive : styles.sidebarDotInactive)} />
                    <span className={cx("text-[14px] font-semibold", isActive ? styles.sidebarLabelActive : styles.sidebarLabelInactive)}>
                      {item.label}
                    </span>
                  </div>

                  <span className={cx("text-[12px] h-[16px] min-w-[28px] px-1.5 flex items-center justify-center rounded-[8px] font-semibold leading-[16px]", isActive ? styles.sidebarCountActive : styles.sidebarCountInactive)}>
                    {item.count}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 bg-white flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto p-3">
              {loading ? (
                <div className="flex justify-center items-center h-full">
                  <Spin tip="載入中..." />
                </div>
              ) : (
                <>
                  {filteredCountryList.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4 content-start">{filteredCountryList.map(renderCountryCard)}</div>
                  ) : (
                    <div className="flex justify-center items-center h-full">
                      <Empty description="暫無資料" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
